import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Layout from './components/Layout';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Agents from './pages/Agents';
import AgentDetail from './pages/AgentDetail';
import Listeners from './pages/Listeners';
import Tasks from './pages/Tasks';
import MapView from './pages/MapView';
import Console from './pages/Console';
import Payloads from './pages/Payloads';
import Settings from './pages/Settings';
import Timeline from './pages/Timeline';
import Cases from './pages/Cases';
import CaseDetail from './pages/CaseDetail';
import Personas from './pages/Personas';
import Evidence from './pages/Evidence';
import Intelligence from './pages/Intelligence';
import AuditLog from './pages/AuditLog';
import Templates from './pages/Templates';
import Indicators from './pages/Indicators';
import IndicatorDetail from './pages/IndicatorDetail';
import Observables from './pages/Observables';
import Actors from './pages/Actors';
import ActorDetail from './pages/ActorDetail';
import Organizations from './pages/Organizations';
import OrganizationDetail from './pages/OrganizationDetail';
import Countries from './pages/Countries';
import IntelligenceSearch from './pages/intelligence/IntelligenceSearch';
import IntelligenceSearchResult from './pages/intelligence/IntelligenceSearchResult';
import { IntelligenceExtractor } from './pages/intelligence/IntelligenceExtractor';
import {
  IntelligenceCompare,
  IntelligenceCorpus,
  IntelligenceHistory,
  IntelligencePanelHunter,
} from './pages/intelligence/IntelligenceTools';
import WikiLayout, { WikiArticle, WikiHome } from './pages/Docs';
import DfrOverview from './pages/dfr/DfrOverview';
import {
  DfrClients,
  DfrCases,
  DfrQueue,
  DfrMonitoring,
  DfrEvidence,
  DfrReports,
  DfrSettings,
} from './pages/dfr';
import ToastNotification from './components/ToastNotification';
import NetworkBackground from './components/NetworkBackground';
import { checkAuth } from './store/authSlice';
import './styles/App.css';

function PrivateRoute({ children }) {
  const { isAuthenticated, loading } = useSelector((state) => state.auth);
  
  if (loading) {
    return (
      <div className="loading-screen">
        <NetworkBackground />
        <div className="loading-screen-content">
          <div className="loader"></div>
          <span className="loading-text">INITIALIZING PLATFORM...</span>
        </div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/" />;
}

function RootRoute() {
  const { isAuthenticated, loading } = useSelector((state) => state.auth);

  if (loading) {
    return (
      <div className="loading-screen">
        <NetworkBackground />
        <div className="loading-screen-content">
          <div className="loader"></div>
          <span className="loading-text">INITIALIZING PLATFORM...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Landing />;
  }

  return (
    <Layout>
      <Dashboard />
    </Layout>
  );
}

function App() {
  const dispatch = useDispatch();
  
  useEffect(() => {
    dispatch(checkAuth());
  }, [dispatch]);
  
  return (
    <div className="app">
      <ToastNotification />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<RootRoute />} />
        <Route
          path="/*"
          element={
            <PrivateRoute>
              <Layout>
                <Routes>
                  <Route path="/intelligence/search" element={<IntelligenceSearch />} />
                  <Route path="/intelligence/search/:queryId" element={<IntelligenceSearchResult />} />
                  <Route path="/intelligence/history" element={<IntelligenceHistory />} />
                  <Route path="/intelligence/extractor" element={<IntelligenceExtractor />} />
                  <Route path="/intelligence/compare" element={<IntelligenceCompare />} />
                  <Route path="/intelligence/corpus" element={<IntelligenceCorpus />} />
                  <Route path="/intelligence/panel-hunter" element={<IntelligencePanelHunter />} />
                  <Route path="/dfr" element={<DfrOverview />} />
                  <Route path="/dfr/clients" element={<DfrClients />} />
                  <Route path="/dfr/cases" element={<DfrCases />} />
                  <Route path="/dfr/queue" element={<DfrQueue />} />
                  <Route path="/dfr/monitoring" element={<DfrMonitoring />} />
                  <Route path="/dfr/evidence" element={<DfrEvidence />} />
                  <Route path="/dfr/reports" element={<DfrReports />} />
                  <Route path="/dfr/settings" element={<DfrSettings />} />
                  <Route path="/cases" element={<Cases />} />
                  <Route path="/cases/:caseId" element={<CaseDetail />} />
                  <Route path="/observables" element={<Observables />} />
                  <Route path="/indicators" element={<Indicators />} />
                  <Route path="/indicators/:indicatorId" element={<IndicatorDetail />} />
                  <Route path="/actors" element={<Actors />} />
                  <Route path="/actors/:actorId" element={<ActorDetail />} />
                  <Route path="/organizations" element={<Organizations />} />
                  <Route path="/organizations/:organizationId" element={<OrganizationDetail />} />
                  <Route path="/countries" element={<Countries />} />
                  <Route path="/personas" element={<Personas />} />
                  <Route path="/evidence" element={<Evidence />} />
                  <Route path="/reports" element={<Intelligence />} />
                  <Route path="/audit" element={<AuditLog />} />
                  <Route path="/agents" element={<Agents />} />
                  <Route path="/agents/:agentId" element={<AgentDetail />} />
                  <Route path="/listeners" element={<Listeners />} />
                  <Route path="/tasks" element={<Tasks />} />
                  <Route path="/map" element={<MapView />} />
                  <Route path="/console" element={<Console />} />
                  <Route path="/payloads" element={<Payloads />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/docs" element={<WikiLayout />}>
                    <Route index element={<WikiHome />} />
                    <Route path=":slug" element={<WikiArticle />} />
                  </Route>
                  <Route path="/timeline" element={<Timeline />} />
                  <Route path="/templates" element={<Templates />} />
                </Routes>
              </Layout>
            </PrivateRoute>
          }
        />
      </Routes>
    </div>
  );
}

export default App;
